import { liveQuery } from 'dexie';
import { create } from 'zustand';
import type { ActiveTimer, Label, Project, Settings } from '@timato/shared';
import { db } from '@/db/db';
import { defaultSettings } from '@/db/importV1';
import { IDLE_TIMER } from '@/timer/engine';
import { notDeleted } from './repo';

/**
 * The rows every screen reads: settings, the active timer, projects and labels.
 *
 * They live in one store fed by long-lived Dexie subscriptions, rather than a `useLiveQuery` per component.
 * A live query starts at its default and fills in a tick later, so a component that mounts on a route change
 * paints the default first - which is how the timer ring used to flash the accent before the project's colour.
 * Subscribing once, above the routes, means a screen always mounts with the values already in memory.
 */
const NO_PROJECTS: Project[] = [];
const NO_LABELS: Label[] = [];
const DEFAULT_SETTINGS = defaultSettings('');

type CoreStore = {
  /** False until every table below has delivered once. The app waits rather than paint defaults. */
  ready: boolean;
  settings: Settings;
  activeTimer: ActiveTimer;
  projects: Project[];
  labels: Label[];
};

export const useCore = create<CoreStore>(() => ({
  ready: false,
  settings: DEFAULT_SETTINGS,
  activeTimer: IDLE_TIMER,
  projects: NO_PROJECTS,
  labels: NO_LABELS,
}));

const pending = new Set(['settings', 'activeTimer', 'projects', 'labels']);

/** Publish one table's rows, and flip `ready` once none are outstanding. */
function publish<K extends keyof CoreStore>(key: K, value: CoreStore[K]) {
  pending.delete(key);
  useCore.setState({ [key]: value, ...(pending.size === 0 && { ready: true }) } as Partial<CoreStore>);
}

let watching = false;

/** Starts the subscriptions. Called once from the entry point; they live as long as the tab does. */
export function watchCoreData() {
  if (watching) return;
  watching = true;
  // A row written by an older build lacks the fields added since; the defaults fill them here, once, for every
  // reader. A new settings field therefore never needs a migration, and no screen may index by a missing one.
  liveQuery(() => db.settings.get('settings')).subscribe((s) => publish('settings', { ...DEFAULT_SETTINGS, ...s }));
  liveQuery(() => db.activeTimer.get('active')).subscribe((t) => publish('activeTimer', t ?? IDLE_TIMER));
  liveQuery(async () => (await db.projects.orderBy('position').toArray()).filter(notDeleted)).subscribe((p) => publish('projects', p));
  liveQuery(async () => (await db.labels.orderBy('position').toArray()).filter(notDeleted)).subscribe((l) => publish('labels', l));
}
